<template>
	<v-container fluid pa-0>
		<v-layout :style="{ height: height }" row wrap>
			<v-flex sm3 lg2 xl2 class="pa-3 output-menu" :style="{ height }">
				<v-autocomplete
					ref="outputInput"
					v-if="outputs"
					v-model="currentOutput"
					:items="outputs"
					:rules="[validation]"
					item-text="name"
					return-object
					label="Output *"
				>
					<template slot="item" slot-scope="data">
						<div>
							{{ data.item.name }}
							<span v-if="data.item.classic" class="caption">
								- Classic
							</span>
							<span v-if="data.item.shared" class="caption">
								- Shared
							</span>
						</div>
					</template>
					<template slot="append-outer" class="text-xs-center">
						<v-icon
							color="secondary"
							style="cursor: pointer;"
							@click="showNewOutput"
						>
							add
						</v-icon>
					</template>
					<div slot="no-data" class="text-xs-center">
						<div class="body">No Outputs found.</div>
						<v-btn
							v-if="outputs.length === 0"
							color="secondary"
							@click="showNewOutput"
						>
							Create New
						</v-btn>
					</div>
				</v-autocomplete>
				<v-autocomplete
					v-if="reports"
					v-model="currentReportId"
					:items="filteredReports"
					:rules="[validation]"
					:filter="autocompleteFilter"
					item-text="displayName"
					item-value="id"
					label="Report *"
					no-data-text="No reports found."
				>
					<template slot="item" slot-scope="data">
						<div>
							{{ data.item.displayName }}
							<span v-if="data.item.date" class="caption">
								- {{ data.item.date }}
							</span>
						</div>
					</template>
				</v-autocomplete>
				<div class="title mb-3 mt-2">Preview:</div>
				<v-radio-group v-model="previewStyle" :disabled="currentOutput.classic" hide-details>
					<v-radio
						v-for="option in previewOptions"
						:key="option.value"
						:label="option.label"
						:value="option.value"
						color="primary"
					/>
				</v-radio-group>
				<OutputSettings
					v-if="showOutputSettings"
					:output-settings="currentOutput"
					@output-settings="submit"
				>
					<div
						v-if="showTemplateSettings"
						slot="additional-settings"
						class="text-xs-center"
					>
						<div class="title my-2 text-xs-left">Customize</div>
						<div class="customize-btn-row">
							<v-btn
								outline
								color="primary"
								class="mx-0"
								@click="customizeOutputArticle = !customizeOutputArticle"
							>
								Article
							</v-btn>
							<v-btn
								v-if="currentOutput.customArticle !== null && currentOutput.customArticle !== ''"
								color="error"
								outline
								@click="resetCustom('customArticle')"
							>
								Reset
							</v-btn>
						</div>
						<div class="customize-btn-row">
							<v-btn
								outline
								color="primary"
								class="mx-0"
								@click="customizeOutputIndex = !customizeOutputIndex"
							>
								Index
							</v-btn>
							<v-btn
								v-if="currentOutput.customIndex !== null && currentOutput.customIndex !== ''"
								outline
								color="error"
								@click="resetCustom('customIndex')"
							>
								Reset
							</v-btn>
						</div>
						<div class="customize-btn-row">
							<v-btn
								outline
								color="primary"
								class="mx-0"
								@click="customizeOutputBody = !customizeOutputBody"
							>
								Body
							</v-btn>
							<v-btn
								v-if="currentOutput.customBody !== null && currentOutput.customBody !== ''"
								outline
								color="error"
								@click="resetCustom('customBody')"
							>
								Reset
							</v-btn>
						</div>
						<div class="customize-btn-row">
							<v-btn
								outline
								color="primary"
								class="mx-0"
								@click="customizeOutputStyles = !customizeOutputStyles"
							>
								CSS
							</v-btn>
							<v-btn
								v-if="currentOutput.customCss !== null && currentOutput.customCss !== ''"
								outline
								color="error"
								@click="resetCustom('customCss')"
							>
								Reset
							</v-btn>
						</div>
					</div>
				</OutputSettings>
				<div v-if="currentOutput.classic" class="title pt-4 text-xs-center">
					This Output is from Report Mule v1, and settings can be adjusted there.
				</div>
			</v-flex>
			<v-flex sm9 lg10 xl10 class="pa-3">

				<p v-if="!showOutput" class="title text-xs-center">
					The preview will show here once {{ missingFieldMessage }} is
					selected.
				</p>

				<div v-show="showOutput" class="preview-container">

					<transition name="fade-transition">
						<div v-show="loading" class="loading-container">
							<p class="title text-xs-center">
								Hang tight - the Mule's building your report.
							</p>
							<loading loading class="pt-4" />
						</div>
					</transition>

					<transition name="fade-transition">
						<div v-show="!loading">
							<p class="title text-xs-center ma-0">
								Share
							</p>
							<v-layout row wrap justify-center>
								<v-tooltip
									v-for="share in sharesList" :key="share.type"
									:class="{ 'superUser': (share.superOnly === true) }"
									top
								>
									<v-btn
										slot="activator"
										:color="share.color"
										:href="share.type === 'table' ? '' : getShareReportUrl(share.type)"
										flat
										icon
										large
										target="_blank"
										@click="share.type === 'table' ? $refs.excelExport.open() : null"
									>
										<v-icon>{{ share.icon }}</v-icon>
									</v-btn>
									<span>{{ share.name }}</span>
								</v-tooltip>
								<ExcelExportDialog
									ref="excelExport"
									:outputKey="key"
									:output="currentOutput"
								/>
							</v-layout>
							<v-card
								v-show="currentReportId && currentOutput"
								class="elevation-4 pa-0"
							>
								<iframe
									id="frame"
									ref="frame"
									:src="frameURL"
									:style="{ height: frameHeight }"
									@load="iframeOnLoad"
								/>
							</v-card>
						</div>
					</transition>

				</div>

			</v-flex>
		</v-layout>
		<v-dialog v-model="customizeOutputArticle" max-width="100%">
			<v-card>
				<OutputCustomize
					v-if="customizeOutputArticle && showOutput"
					:initial-markdown="currentOutput.customArticle"
					:report="currentReport"
					title="Customize Article Output"
					outputPreview="article_body"
					:outputKey="key"
					@save="saveCustom('customArticle', $event)"
					@close="customizeOutputArticle = false"
				/>
			</v-card>
		</v-dialog>
		<v-dialog v-model="customizeOutputIndex" max-width="100%">
			<v-card>
				<OutputCustomize
					v-if="customizeOutputIndex && showOutput"
					:initial-markdown="currentOutput.customIndex"
					:report="currentReport"
					title="Customize Index Output"
					outputPreview="article_index"
					:outputKey="key"
					@save="saveCustom('customIndex', $event)"
					@close="customizeOutputIndex = false"
				/>
			</v-card>
		</v-dialog>
		<v-dialog v-model="customizeOutputBody" max-width="100%">
			<v-card>
				<OutputCustomize
					v-if="customizeOutputBody && showOutput"
					:initial-markdown="currentOutput.customBody"
					:report="currentReport"
					:article-related="false"
					title="Customize Body Output"
					outputPreview="body"
					:outputKey="key"
					@save="saveCustom('customBody', $event)"
					@close="customizeOutputBody = false"
				/>
			</v-card>
		</v-dialog>
		<v-dialog v-model="customizeOutputStyles" max-width="80%">
			<v-card>
				<OutputCSS
					v-if="customizeOutputStyles && showOutput"
					:initial="currentOutput.customCss"
					@save="saveCustom('customCss', $event)"
					@close="customizeOutputStyles = false"
				/>
			</v-card>
		</v-dialog>
		<v-dialog v-model="newOutput" max-width="800px">
			<v-card>
				<v-container>
					<OutputNewForm
						:output-settings-loaded="true"
						@new="currentOutput = $event"
						@close="newOutput = false"
					/>
				</v-container>
			</v-card>
		</v-dialog>
		<FullDialog
			:dialog="editArticle.show"
			dialog-title="Edit Article"
			@close-dialog="editArticle.show = false"
		>
			<template
				v-if="editArticle.show"
				slot="dialog-content"
			>
				<EditArticle
					:article="editArticle.article"
					single-edit
					@saving-done="savingDone"
					@close="editArticle.show = false"
					@set-image="setImageUpload"
				/>
			</template>
		</FullDialog>
		<v-dialog
			ref="dialog"
			v-model="imageUpload.show"
			max-width="800px"
		>
			<v-card v-if="imageUpload.show" class="px-3">
				<ImageUpload
					:item="imageUpload.article"
					closable
					@close="imageUpload.show = false"
				/>
			</v-card>
		</v-dialog>
		<Confirm ref="confirm" />
	</v-container>
</template>

<script>
import OutputSettings from '@/components/Report/OutputSettings';
import { Article } from '@/models/Article';
import { OutputKey } from '@/models/Output';
import OutputNewForm from '@/components/Report/OutputNewForm';
import ExcelExportDialog from '@/components/Output/ExcelExportDialog';
import StoreMethods from '@/mixins/StoreMethods';
import FullDialog from '@/components/FullDialog';
import Confirm from '@/components/Confirm';
import EditArticle from '@/components/Article/EditArticle';
import ArticlesListMixins from '@/mixins/ArticlesListMixins';
import ImageUpload from '@/components/ImageUpload';
import { sync } from 'vuex-pathify';

export default {
	components: {
		OutputSettings,
		OutputCustomize: () => import('./OutputCustomize'),
		OutputCSS: () => import('./OutputCSS'),
		OutputNewForm,
		ExcelExportDialog,
		EditArticle,
		FullDialog,
		Confirm,
		ImageUpload,
	},
	props: {
		outputs: { type: Array, default: null },
		reports: { type: Array, default: null },
		initialOutput: { type: Object, default: null },
		initialReport: { type: Object, default: null },
	},
	mixins: [StoreMethods, ArticlesListMixins],
	data() {
		return {
			loading: true,
			currentReportId: null,
			currentOutput: null,
			shares: [
				{ name: 'Get Printable Report', icon: 'fa fa-print', color: 'primary', type: 'print' },
				{ name: 'Get Shareable URL', icon: 'fa fa-globe', color: 'secondary', type: 'web' },
				{ name: 'Get PDF', icon: 'fa fa-file-pdf-o', color: 'red', type: 'pdf', superOnly: true },
				{ name: 'Get Word Doc.', icon: 'fa fa-file-word-o', color: 'indigo', type: 'word', superOnly: true },
				{ name: 'Get Table Report', icon: 'fa fa-table', color: 'green', type: 'table' },
				{ name: 'Get Powerpoint Report', icon: 'fa fa-file-powerpoint-o', color: 'orange', type: 'powerpoint' },
			],
			classicShares: [
				{ name: 'Get Report Link', icon: 'fa fa-link', color: 'secondary', type: 'print' },
				{ name: 'Get PDF', icon: 'fa fa-file-pdf-o', color: 'red', type: 'pdf' },
				{ name: 'Get Word Doc.', icon: 'fa fa-file-word-o', color: 'indigo', type: 'word' },
				{ name: 'Get Excel Report', icon: 'fa fa-file-excel-o', color: 'green', type: 'excel' },
				{ name: 'Get CSV Report', icon: 'fa fa-table', color: 'primary', type: 'csv' },
			],
			key: null,
			frameURL: '',
			previewOptions: [
				{ label: 'Print', value: 'print' },
				{ label: 'Web', value: 'web' },
			],
			previewStyle: 'web',
			validation: v => !!v || 'Field is required',
			customizeOutputArticle: false,
			customizeOutputIndex: false,
			customizeOutputBody: false,
			customizeOutputStyles: false,
			newOutput: false,
			scrollTo: NaN,
		};
	},
	computed: {
		filteredReports() {
			const filteredReports = this.reports
				.filter(report => report.stats.activeArticlesCount > 0);
			// Allows archived/empty reports to still output self.
			if (this.initialReport) {
				filteredReports.push(this.initialReport);
			}
			return filteredReports.sort((a, b) => new Date(b.date) - new Date(a.date));
		},
		currentReport() {
			return this.reports.find(report => report.id === this.currentReportId);
		},
		currentProject() {
			return this.$store.get('projects/currentProject');
		},
		height() {
			if (this.$vuetify.breakpoint.mdAndUp) {
				return `${window.innerHeight - 64}px`;
			}
			return '';
		},
		frameHeight() {
			if (this.$vuetify.breakpoint.mdAndUp) {
				return `${window.innerHeight - 200}px`;
			}
			return `${window.innerHeight - 60}px`;
		},
		baseURL() {
			if (this.currentProject.customDomain) {
				return `https://${this.currentProject.customDomain}`;
			}

			return process.env.VUE_APP_OUTPUT_ROOT;
		},
		missingFieldMessage() {
			if (!this.currentOutput) {
				return 'an output';
			}
			return 'a report';
		},
		showOutput() {
			return this.currentReportId && this.currentOutput;
		},
		sharesList() {
			if (!this.currentOutput) {
				return [];
			}
			if (this.currentOutput.classic) {
				return this.classicShares;
			}
			return this.shares.filter(v => v.superOnly !== true || this.isSuperUser);
		},
		isSuperUser() {
			return this.$store.get('superUser');
		},
		showOutputSettings() {
			return this.showOutput
					&& !this.currentOutput.classic
					&& !this.currentReport.isArchivedOrParentArchived;
		},
		showTemplateSettings() {
			return this.showOutput
					&& !this.currentReport.isArchivedOrParentArchived
					&& this.$userCan('change_output_template');
		},
		articlesLoading: sync('events/articlesLoading'),
	},
	watch: {
		// Refresh frame after article changed (moved, copied, or deleted)
		articlesLoading(val) {
			if (val.changing) {
				this.refreshFrame();
			}
		},
		previewStyle() {
			if (this.showOutput) {
				this.getOutputURL();
			}
		},
		/** @todo Improve */
		currentReportId(newVal, oldVal) {
			if (this.showOutput) {
				this.getOutputURL();
			}
		},
		/** @todo Improve */
		currentOutput(newVal, oldVal) {
			if (this.showOutput) {
				this.getOutputURL();
			}
			if (newVal.classic) {
				this.previewStyle = 'print';
			}
		},
		initialReport(val) {
			this.currentReport = val;
		},
		initialOutput(val) {
			this.currentOutput = val;
		},
	},
	created() {
		this.setStoreOutputSettings();

		if (this.initialReport) {
			this.currentReportId = this.initialReport.id;
		}
		if (this.initialOutput) {
			this.currentOutput = this.initialOutput;
		}
		else if (this.outputs) {
			this.currentOutput = this.outputs[0];
		}

		if (this.showOutput)
			this.getOutputURL();
	},
	mounted() {
		window.addEventListener('message', this.iframeMessageHandler);
	},
	beforeDestroy() {
		window.removeEventListener('message', this.iframeMessageHandler);
	},
	methods: {
		iframeMessageHandler(event) {
			switch (event.data.action) {
			case 'editArticle':
				if (event.data.articleId) {
					this.setEditArticle(event.data.articleId);
				}
				break;
			case 'liveEdit':
				this.liveEdit(event.data);
				break;
			default:
				console.log(`Unknown iframe message: ${event.data.action}`);
			}

			if (event.data.scroll) {
				this.scrollTo = event.data.scroll;
			}
		},
		liveEdit(data) {
			const article = new Article();
			article.id = data.pk;
			article.fetch().then(() => {
				article[data.attribute] = data.value;
				article.save();
			}).catch((e) => {
				this.$notify('error', `Failed to update article's ${data.attribute}`);
			});
		},
		autocompleteFilter(item, queryText) {
			const contains = item.displayName
				.toLowerCase()
				.indexOf(queryText.toLowerCase());
			if (contains > -1) {
				return true;
			}
			return false;
		},
		getOutputURL() {
			this.loading = true;
			const key = new OutputKey();
			key.id = this.currentOutput.id;
			key.reportId = this.currentReportId;
			key.fetch()
				.then(() => {
					const url = `${this.baseURL}/${this.previewStyle}/${key.key}`;
					this.key = key.key;
					this.frameURL = url;
				})
				.catch(() => {
					this.$notify('error', 'Failed to get output preview');
				});
		},
		getShareReportUrl(type) {
			return `${this.baseURL}/${type}/${this.key}`;
		},
		refreshFrame() {
			// Reload current url by simulating a change with extra space
			this.loading = true;
			this.frameURL = `${this.frameURL} `;
		},
		savingDone() {
			this.editArticle.show = false;
			this.refreshFrame();
		},
		submit(output) {
			this.loading = true;
			output
				.save()
				.then(() => {
					this.$notify(
						'success',
						`Output ${output.name} has been updated`,
					);
					if (this.showOutput) {
						this.refreshFrame();
					}
				})
				.catch(() => {
					this.$notify('error', `Failed to updated ${output.name}`);
					this.loading = false;
				});
		},
		saveCustom(attribute, event) {
			this.currentOutput.set(attribute, event);
			this.submit(this.currentOutput);
		},
		resetCustom(attribute) {
			this.$refs.confirm
				.createConfirm('Reset output attribute to default?', 'error')
				.then((answer) => {
					if (!answer) {
						return;
					}
					this.currentOutput.set(attribute, null);
					this.submit(this.currentOutput);
				});
		},
		showNewOutput() {
			this.$refs.outputInput.blur();
			this.newOutput = true;
		},
		iframeOnLoad() {
			console.log('iframeOnLoad');
			if (this.frameURL) {
				this.loading = false;
			}
			const iframe = this.$refs.frame;
			if (iframe && this.scrollTo) {
				iframe.contentWindow.scrollTo(0, this.scrollTo);
				console.log(`Scroll to ${this.scrollTo}`);
				this.scrollTo = NaN;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.preview-container {
	position: relative;
	.loading-container {
		position: absolute;
		width: 100%;
	}
}
iframe {
	width: 100%;
	// height: 75vh;
	// min-height: 600px;
	border: none;
	display: block;
	// z-index: 3;
}
.v-card {
	z-index: 0;
}
.output-menu {
	overflow-y: auto;
}
.iframe-scroll-btn {
	margin-bottom: 3rem;
}
.customize-btn-row {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	> button {
		width: 50%;
	}
}
.beta-chip {
	font-size: 9px;
	height: 18px !important;
	position: absolute;
	right: -41px;
	top: -11px;
}
</style>
